import type {
  AppState,
  Revenu,
  Charge,
  FreelanceCharge,
  Salarie,
  Dirigeant,
  AutreTaxe,
  Charges,
  SalaireCategorie,
  Investissement,
} from '../types';
import { normalizeArrayNums, normalizeArrayNulls } from '../lib/array36';
import { createDefaultAppState } from '../lib/defaultState';
import { generateId } from '../lib/utils';
import { inferSalaireCategory } from '../lib/salaireCategoryResolve';
import { DEFAULT_DIRIGEANT_TAUX_CHARGES } from '../lib/dirigeantDefaults';

/** Normalise la liste des dirigeants (migration depuis l’ancien objet unique `dirigeant`). */
function normalizeDirigeantsFromLoad(state: AppState, n: number = 36): Dirigeant[] {
  const legacy = (state as unknown as { dirigeant?: Partial<Dirigeant> & { modeNet?: boolean } }).dirigeant;
  let list = state.dirigeants;
  if (!Array.isArray(list) || list.length === 0) {
    if (legacy && typeof legacy === 'object') {
      const base = legacy as Partial<Dirigeant> & { modeNet?: boolean };
      list = [
        {
          id: base.id && String(base.id).trim() ? String(base.id) : generateId(),
          nom: base.nom ?? 'Dirigeant',
          notes: base.notes ?? '',
          modeSaisie: (base.modeSaisie && ['brut','net','cout'].includes(base.modeSaisie) ? base.modeSaisie : (base.modeNet ? 'net' : 'brut')) as 'brut'|'net'|'cout',
          regime: base.regime ?? 'custom',
          customTaux: Number(base.customTaux ?? 0),
          remuMode: base.remuMode !== false,
          remuUniform: Number(base.remuUniform ?? 0),
          remuMensuels: normalizeArrayNums(base.remuMensuels ?? [], n),
          remuReels: normalizeArrayNulls(base.remuReels, n),
          dividendes: [
            base.dividendes?.[0] ?? 0,
            base.dividendes?.[1] ?? 0,
            base.dividendes?.[2] ?? 0,
          ],
        },
      ];
    } else {
      list = [makeDirigeant(state.meta?.defaultTauxDirigeant ?? DEFAULT_DIRIGEANT_TAUX_CHARGES)];
    }
  }
  return list.map((d) => ({
    ...d,
    id: d.id && String(d.id).trim() ? d.id : generateId(),
    nom: d.nom ?? 'Dirigeant',
    notes: d.notes ?? '',
    remuMensuels: normalizeArrayNums(d.remuMensuels, n),
    remuReels: normalizeArrayNulls(d.remuReels, n),
    dividendes: [d.dividendes?.[0] ?? 0, d.dividendes?.[1] ?? 0, d.dividendes?.[2] ?? 0],
  }));
}

export type Action =
  | { type: 'META_UPDATE'; payload: Partial<AppState['meta']> }
  // Revenus
  | { type: 'REVENU_ADD' }
  | { type: 'REVENU_DUPLICATE'; id: string }
  | { type: 'REVENU_UPDATE'; id: string; payload: Partial<Revenu> }
  | { type: 'REVENU_REMOVE'; id: string }
  | { type: 'REVENU_REMOVE_BULK'; ids: string[] }
  | { type: 'REVENU_MONTH_UPDATE'; id: string; mi: number; value: number }
  | { type: 'REVENU_PROG_PCT_MONTH'; id: string; mi: number; value: number }
  // Charges
  | { type: 'CHARGE_ADD'; cat: keyof AppState['charges'] }
  | { type: 'CHARGE_DUPLICATE'; cat: keyof AppState['charges']; id: string }
  | {
      type: 'CHARGE_UPDATE';
      cat: keyof AppState['charges'];
      id: string;
      payload: Partial<Charge & FreelanceCharge>;
      /** Si défini et différent de `cat`, la charge est déplacée vers cette catégorie (avec conversion Prestataire ↔ charge classique si besoin). */
      toCat?: keyof AppState['charges'];
    }
  | { type: 'CHARGE_REMOVE'; cat: keyof AppState['charges']; id: string }
  | { type: 'CHARGE_REMOVE_BULK'; items: { cat: keyof AppState['charges']; id: string }[] }
  | { type: 'CHARGE_MONTH_UPDATE'; cat: keyof AppState['charges']; id: string; mi: number; value: number }
  // Salaires
  | { type: 'SALARIE_ADD' }
  | { type: 'SALARIE_DUPLICATE'; id: string }
  | { type: 'SALARIE_UPDATE'; id: string; payload: Partial<Salarie> }
  | { type: 'SALAIRE_CATEGORIE_ADD'; label?: string }
  | { type: 'SALAIRE_CATEGORIE_UPDATE'; id: string; label: string }
  | { type: 'SALAIRE_CATEGORIE_REMOVE'; id: string }
  | { type: 'SALARIE_REMOVE'; id: string }
  | { type: 'SALARIE_REMOVE_BULK'; ids: string[] }
  | { type: 'SALARIE_BULK_SET_CATEGORIE'; ids: string[]; categorieId: string | null }
  /** Renseigne categorieId à partir du poste / nom (libellé de catégorie trouvé dans le texte). */
  | { type: 'SALARIE_APPLY_INFERRED_CATEGORIES' }
  | { type: 'SALARIE_BONUS_UPDATE'; id: string; mi: number; value: number }
  // Dirigeants
  | { type: 'DIRIGEANT_ADD' }
  | { type: 'DIRIGEANT_DUPLICATE'; id: string }
  | { type: 'DIRIGEANT_REMOVE'; id: string }
  | { type: 'DIRIGEANT_UPDATE'; id: string; payload: Partial<Dirigeant> }
  | { type: 'DIRIGEANT_REMU_MONTH'; id: string; mi: number; value: number }
  | { type: 'DIVIDENDE_UPDATE'; id: string; ex: number; value: number }
  // Fiscalité
  | { type: 'FISCALITE_UPDATE'; payload: Partial<AppState['fiscalite']> }
  | { type: 'CFE_UPDATE'; ex: number; value: number }
  | { type: 'TAXE_ADD' }
  | { type: 'TAXE_REMOVE'; id: string }
  | { type: 'TAXE_UPDATE'; id: string; payload: Partial<AutreTaxe> }
  // Réel
  | { type: 'REEL_UPDATE'; field: keyof AppState['reel']; mi: number; value: number | null }
  // Réel par ligne
  | { type: 'REVENU_REEL_UPDATE'; id: string; mi: number; value: number | null }
  | { type: 'CHARGE_REEL_UPDATE'; cat: keyof AppState['charges']; id: string; mi: number; value: number | null }
  | { type: 'SALARIE_REEL_UPDATE'; id: string; mi: number; value: number | null }
  | { type: 'DIRIGEANT_REEL_MONTH'; id: string; mi: number; value: number | null }
  // Investissements
  | { type: 'INVESTISSEMENT_ADD'; payload: Omit<Investissement, 'id'> }
  | { type: 'INVESTISSEMENT_UPDATE'; id: string; payload: Partial<Investissement> }
  | { type: 'INVESTISSEMENT_REMOVE'; id: string }
  // Budget salaires
  | { type: 'SALAIRES_BUDGET_MONTH_UPDATE'; mi: number; value: number }
  | { type: 'SALAIRES_BUDGET_MODE_UPDATE'; mode: 'fixed' | 'pct' }
  | { type: 'SALAIRES_BUDGET_PCT_UPDATE'; pct: number }
  // Notaires
  | { type: 'NOTAIRES_CONFIG_UPDATE'; payload: Partial<AppState['notairesConfig']> }
  /**
   * Synchronise les salariés virtuels gérés par le module notaires.
   * Remplace atomiquement toutes les lignes dont l'id commence par `notaire-auto-`
   * par le tableau fourni. Les salariés saisis manuellement ne sont pas touchés.
   */
  | { type: 'NOTAIRES_SALARIES_SYNC'; salaries: Salarie[] }
  /** Saisit (ou efface) le coût réel mensuel de sous-traitance notaires pour le mois `mi`. */
  | { type: 'SOUS_TRAITANCE_REEL_UPDATE'; mi: number; value: number | null }
  /**
   * Met à jour (ou efface si `value = null`) un override mensuel du mode Réel de la Synthèse
   * pour une ligne agrégée donnée. `key` identifie la ligne ciblée (voir `ReelOverrides`).
   */
  | { type: 'REEL_OVERRIDE_UPDATE'; key: keyof AppState['reelOverrides']; mi: number; value: number | null }
  // UI
  | { type: 'UI_SET_PERIODE'; periode: AppState['ui']['periode'] }
  | {
      type: 'UI_SET_DASHBOARD_MONTH_RANGE';
      fromMi: number | null;
      toMi: number | null;
    }
  | { type: 'UI_SET_SYNTHESE_PERIODE'; periode: string }
  // State lifecycle
  | { type: 'STATE_LOAD'; state: AppState }
  | { type: 'STATE_RESET' };

/** Calcule le nombre total de mois depuis les durées d'exercice. */
function calcTotalMi(meta: AppState['meta']): number {
  const d1 = Math.max(1, Math.round(meta?.exercice1Duree ?? 12));
  const d2 = Math.max(1, Math.round(meta?.exercice2Duree ?? 12));
  const d3 = Math.max(1, Math.round(meta?.exercice3Duree ?? 12));
  return d1 + d2 + d3;
}
const nullN = (n: number) => Array<number | null>(n).fill(null);

/**
 * Fusionne les overrides réels chargés depuis le state persisté (potentiellement absents ou
 * partiels, en particulier pour les anciens forecasts) avec la forme par défaut. Chaque
 * tableau est redimensionné à la longueur `n` (total de mois courant).
 */
function mergeReelOverrides(
  loaded: Partial<AppState['reelOverrides']> | undefined,
  n: number,
): AppState['reelOverrides'] {
  const defaults = createDefaultAppState().reelOverrides;
  const out = { ...defaults } as AppState['reelOverrides'];
  (Object.keys(defaults) as (keyof AppState['reelOverrides'])[]).forEach((k) => {
    out[k] = normalizeArrayNulls(loaded?.[k], n);
  });
  return out;
}

/** Normalise une charge fusionnée (payload) vers la forme attendue pour la catégorie cible. */
function shapeChargeForCategory(
  merged: Charge & FreelanceCharge & { libelle?: string; nom?: string; mission?: string },
  destCat: keyof AppState['charges'],
  n: number = 36,
): Charge | FreelanceCharge {
  const m = normalizeArrayNums(merged.montantsMensuels, n);
  const reels = normalizeArrayNulls(merged.montantsReels, n);
  const common = {
    id: merged.id,
    mode: merged.mode,
    montantUniforme: merged.montantUniforme,
    montantsMensuels: m,
    montantsReels: reels,
    progStart: merged.progStart,
    progBase: merged.progBase,
    progPct: merged.progPct,
    progPctExercices: merged.progPctExercices ?? null,
    variableRevenuId: merged.variableRevenuId ?? null,
    variableChargeId: merged.variableChargeId ?? null,
    variablePct: merged.variablePct ?? 0,
    variablePctExercices: merged.variablePctExercices ?? null,
    variablePctMensuels: merged.variablePctMensuels ?? null,
    finMois: merged.finMois ?? null,
    notes: merged.notes ?? '',
    deductible: merged.deductible ?? true,
    tauxTVA: merged.tauxTVA ?? 20,
  };
  if (destCat === 'freelances') {
    const nom = merged.nom ?? merged.libelle ?? '';
    const mission = merged.mission ?? '';
    return { ...common, nom, mission };
  }
  const libelle = merged.libelle ?? merged.nom ?? '';
  return { ...common, libelle };
}


function makeSalarie(defaultTaux: number = 42, n: number = 36): Salarie {
  const taux = defaultTaux === 42 ? '0.42' as const : defaultTaux === 50 ? '0.50' as const : 'custom' as const;
  return {
    id: generateId(),
    nom: 'Nouveau salarié',
    poste: '',
    categorieId: null,
    moisEntree: 0,
    moisSortie: null,
    brut: 0,
    tauxPatronal: taux,
    customTaux: defaultTaux,
    primes: Array(n).fill(0),
    brutsReels: nullN(n),
    notes: '',
  };
}

function makeDirigeant(defaultTaux: number = DEFAULT_DIRIGEANT_TAUX_CHARGES, n: number = 36): Dirigeant {
  return {
    id: generateId(),
    nom: 'Nouveau dirigeant',
    notes: '',
    modeSaisie: 'brut' as const,
    regime: 'custom',
    customTaux: defaultTaux,
    remuMode: true,
    remuUniform: 0,
    remuMensuels: Array(n).fill(0),
    remuReels: nullN(n),
    dividendes: [0, 0, 0],
  };
}

function cloneNullableN(arr: (number | null)[] | undefined, n: number): (number | null)[] {
  return Array.from({ length: n }, (_, i) => {
    if (!arr || i >= arr.length) return null;
    const v = arr[i];
    return v == null ? null : v;
  });
}

function duplicateRevenu(r: Revenu, n: number): Revenu {
  return {
    ...r,
    id: generateId(),
    libelle: `${r.libelle} (copie)`,
    montantsMensuels: [...normalizeArrayNums(r.montantsMensuels, n)],
    montantsReels: cloneNullableN(r.montantsReels, n),
    progPctMensuels: [...normalizeArrayNums(r.progPctMensuels, n)],
  };
}

function duplicateChargeItem(
  item: Charge | FreelanceCharge,
  cat: keyof AppState['charges'],
  n: number,
): Charge | FreelanceCharge {
  const reels = cloneNullableN(item.montantsReels, n);
  const merged = {
    ...item,
    id: generateId(),
    montantsMensuels: [...normalizeArrayNums(item.montantsMensuels, n)],
    montantsReels: reels,
  } as Charge & FreelanceCharge & { libelle?: string; nom?: string; mission?: string };
  if (cat === 'freelances') {
    const f = item as FreelanceCharge;
    merged.nom = `${f.nom} (copie)`;
    merged.mission = f.mission;
  } else {
    const ch = item as Charge;
    merged.libelle = `${ch.libelle} (copie)`;
  }
  return shapeChargeForCategory(merged, cat, n);
}

function duplicateSalarie(s: Salarie, n: number): Salarie {
  return {
    ...s,
    id: generateId(),
    nom: `${s.nom} (copie)`,
    primes: [...normalizeArrayNums(s.primes, n)],
    brutsReels: cloneNullableN(s.brutsReels, n),
  };
}

function removeChargeIdFromCategory<K extends keyof Charges>(
  charges: Charges,
  cat: K,
  id: string,
): Charges {
  const list = charges[cat] as Array<Charge | FreelanceCharge>;
  const filtered = list.filter((c) => c.id !== id);
  return { ...charges, [cat]: filtered as Charges[K] };
}

function updateMonthInArray(arr: number[], mi: number, value: number): number[] {
  const newArr = [...arr];
  newArr[mi] = value;
  return newArr;
}

function updateNullableMonth(arr: (number | null)[], mi: number, value: number | null): (number | null)[] {
  const newArr = [...arr];
  newArr[mi] = value;
  return newArr;
}

/**
 * Recalcule les montants à partir du mois `fromMi` : pour chaque k > fromMi,
 * montant[k] = arrondi(montant[k-1] × (1 + progPct[k]/100)).
 * Les tableaux sont normalisés sur `totalMi` mois.
 */
function cascadeRevenuMontantsFrom(
  montants: number[],
  progPctMensuels: number[],
  fromMi: number,
  totalMi: number,
): number[] {
  const m = [...normalizeArrayNums(montants, totalMi)];
  const p = normalizeArrayNums(progPctMensuels, totalMi);
  for (let k = fromMi + 1; k < totalMi; k++) {
    m[k] = Math.round(m[k - 1] * (1 + p[k] / 100));
  }
  return m;
}

export function appStateReducer(state: AppState, action: Action): AppState {
  // Total months derived from meta (used everywhere instead of hardcoded 36)
  const totalMi = calcTotalMi(state.meta);

  /** Crée un revenu vierge de la bonne longueur */
  function makeRevenu(): Revenu {
    return {
      id: generateId(),
      libelle: 'Nouvelle ligne de revenu',
      categorie: 'Service',
      mode: 'uniform',
      montantUniforme: 0,
      debutMois: 0,
      montantsMensuels: Array(totalMi).fill(0),
      montantsReels: nullN(totalMi),
      progStart: 0, progBase: 0, progPct: 0,
      progPctMensuels: Array(totalMi).fill(0),
      notes: '',
    };
  }
  function makeCharge(): Charge {
    return {
      id: generateId(),
      libelle: 'Nouvelle charge',
      mode: 'uniform',
      montantUniforme: 0,
      montantsMensuels: Array(totalMi).fill(0),
      montantsReels: nullN(totalMi),
      progStart: 0, progBase: 0, progPct: 0, progPctExercices: null,
      variableRevenuId: null, variableChargeId: null, variablePct: 0, variablePctExercices: null, variablePctMensuels: null,
      finMois: null, notes: '',
      deductible: true, tauxTVA: 20,
    };
  }
  function makeFreelance(): FreelanceCharge {
    return {
      id: generateId(),
      nom: 'Nouveau prestataire',
      mission: '',
      mode: 'uniform',
      montantUniforme: 0,
      montantsMensuels: Array(totalMi).fill(0),
      montantsReels: nullN(totalMi),
      progStart: 0, progBase: 0, progPct: 0, progPctExercices: null,
      variableRevenuId: null, variableChargeId: null, variablePct: 0, variablePctExercices: null, variablePctMensuels: null,
      finMois: null, notes: '',
      deductible: true, tauxTVA: 20,
    };
  }

  switch (action.type) {
    case 'META_UPDATE': {
      const newMeta = { ...state.meta, ...action.payload };
      if (action.payload.logoUrl !== undefined)
        localStorage.setItem('app_logo_url', action.payload.logoUrl);
      // Si la durée d'un exercice change, étendre/réduire les tableaux mensuels
      const dureeChanged =
        action.payload.exercice1Duree !== undefined ||
        action.payload.exercice2Duree !== undefined ||
        action.payload.exercice3Duree !== undefined;
      if (!dureeChanged) return { ...state, meta: newMeta };
      const newTotalMi = calcTotalMi(newMeta);
      const extendNums = (arr: number[]) => normalizeArrayNums(arr, newTotalMi);
      const extendNulls = (arr: (number | null)[] | undefined) => normalizeArrayNulls(arr, newTotalMi);
      return {
        ...state,
        meta: newMeta,
        revenus: state.revenus.map((r) => ({
          ...r,
          montantsMensuels: extendNums(r.montantsMensuels),
          montantsReels: extendNulls(r.montantsReels),
          progPctMensuels: extendNums(r.progPctMensuels),
        })),
        charges: {
          capex: state.charges.capex.map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          marketing: state.charges.marketing.map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          outils: state.charges.outils.map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          tech: state.charges.tech.map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          banque: (state.charges.banque ?? []).map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          legal: (state.charges.legal ?? []).map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          locaux: (state.charges.locaux ?? []).map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          vehicules: (state.charges.vehicules ?? []).map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          autres: state.charges.autres.map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
          freelances: state.charges.freelances.map((c) => ({ ...c, montantsMensuels: extendNums(c.montantsMensuels), montantsReels: extendNulls(c.montantsReels) })),
        },
        salaries: state.salaries.map((s) => ({
          ...s,
          primes: extendNums(s.primes),
          brutsReels: extendNulls(s.brutsReels),
        })),
        dirigeants: state.dirigeants.map((d) => ({
          ...d,
          remuMensuels: extendNums(d.remuMensuels),
          remuReels: extendNulls(d.remuReels),
        })),
        reelOverrides: mergeReelOverrides(state.reelOverrides, newTotalMi),
        notairesConfig: {
          ...state.notairesConfig,
          sousTraitanceReels: state.notairesConfig?.sousTraitanceReels
            ? extendNulls(state.notairesConfig.sousTraitanceReels)
            : state.notairesConfig?.sousTraitanceReels,
        },
      };
    }

    case 'REVENU_ADD':
      return { ...state, revenus: [...state.revenus, makeRevenu()] };

    case 'REVENU_DUPLICATE': {
      const idx = state.revenus.findIndex((r) => r.id === action.id);
      if (idx === -1) return state;
      const copy = duplicateRevenu(state.revenus[idx], totalMi);
      const next = [...state.revenus.slice(0, idx + 1), copy, ...state.revenus.slice(idx + 1)];
      return { ...state, revenus: next };
    }

    case 'REVENU_UPDATE':
      return {
        ...state,
        revenus: state.revenus.map((r) => {
          if (r.id !== action.id) return r;
          const next = { ...r, ...action.payload };
          return { ...next, progPctMensuels: normalizeArrayNums(next.progPctMensuels, totalMi) };
        }),
      };

    case 'REVENU_REMOVE':
      return { ...state, revenus: state.revenus.filter((r) => r.id !== action.id) };

    case 'REVENU_REMOVE_BULK':
      if (action.ids.length === 0) return state;
      return {
        ...state,
        revenus: state.revenus.filter((r) => !action.ids.includes(r.id)),
      };

    case 'REVENU_MONTH_UPDATE':
      return {
        ...state,
        revenus: state.revenus.map((r) => {
          if (r.id !== action.id) return r;
          const p = normalizeArrayNums(r.progPctMensuels, totalMi);
          const m = [...normalizeArrayNums(r.montantsMensuels, totalMi)];
          const rounded = Math.round(action.value);
          m[action.mi] = rounded;
          if (action.mi > 0) {
            const prev = m[action.mi - 1];
            if (prev !== 0) {
              p[action.mi] = (rounded / prev - 1) * 100;
            }
          }
          const newMontants = cascadeRevenuMontantsFrom(m, p, action.mi, totalMi);
          return { ...r, montantsMensuels: newMontants, progPctMensuels: p };
        }),
      };

    case 'REVENU_PROG_PCT_MONTH': {
      return {
        ...state,
        revenus: state.revenus.map((r) => {
          if (r.id !== action.id) return r;

          const newPct = updateMonthInArray(
            normalizeArrayNums(r.progPctMensuels, totalMi),
            action.mi,
            action.value,
          );

          const m = [...normalizeArrayNums(r.montantsMensuels, totalMi)];
          if (action.mi > 0) {
            m[action.mi] = Math.round(m[action.mi - 1] * (1 + action.value / 100));
          }
          const newMontants = cascadeRevenuMontantsFrom(m, newPct, action.mi, totalMi);

          return { ...r, progPctMensuels: newPct, montantsMensuels: newMontants };
        }),
      };
    }

    case 'CHARGE_ADD': {
      const item = action.cat === 'freelances' ? makeFreelance() : makeCharge();
      return {
        ...state,
        charges: {
          ...state.charges,
          [action.cat]: [...(state.charges[action.cat] as unknown[]), item],
        },
      };
    }

    case 'CHARGE_DUPLICATE': {
      const { cat, id } = action;
      const list = state.charges[cat] as Array<Charge | FreelanceCharge>;
      const idx = list.findIndex((c) => c.id === id);
      if (idx === -1) return state;
      const shaped = duplicateChargeItem(list[idx], cat, totalMi);
      const newList = [...list.slice(0, idx + 1), shaped, ...list.slice(idx + 1)];
      return { ...state, charges: { ...state.charges, [cat]: newList } };
    }

    case 'CHARGE_UPDATE': {
      const { cat, id, payload, toCat } = action;
      const list = state.charges[cat] as Array<Charge | FreelanceCharge>;
      const item = list.find((c) => c.id === id);
      if (!item) return state;

      const merged = { ...item, ...payload } as Charge & FreelanceCharge & {
        libelle?: string;
        nom?: string;
        mission?: string;
      };
      const destCat = toCat ?? cat;
      const shaped = shapeChargeForCategory(merged, destCat, totalMi);

      if (destCat === cat) {
        return {
          ...state,
          charges: {
            ...state.charges,
            [cat]: list.map((c) => (c.id === id ? shaped : c)),
          },
        };
      }

      const without = list.filter((c) => c.id !== id);
      const destList = state.charges[destCat] as Array<Charge | FreelanceCharge>;
      return {
        ...state,
        charges: {
          ...state.charges,
          [cat]: without,
          [destCat]: [...destList, shaped],
        },
      };
    }

    case 'CHARGE_REMOVE':
      return {
        ...state,
        charges: {
          ...state.charges,
          [action.cat]: (state.charges[action.cat] as Array<{ id: string }>).filter(
            (c) => c.id !== action.id
          ),
        },
      };

    case 'CHARGE_REMOVE_BULK': {
      if (action.items.length === 0) return state;
      let nextCharges: Charges = { ...state.charges };
      for (const { cat, id } of action.items) {
        nextCharges = removeChargeIdFromCategory(nextCharges, cat, id);
      }
      return { ...state, charges: nextCharges };
    }

    case 'CHARGE_MONTH_UPDATE':
      return {
        ...state,
        charges: {
          ...state.charges,
          [action.cat]: (state.charges[action.cat] as Array<{ id: string; montantsMensuels: number[] }>).map((c) =>
            c.id === action.id
              ? { ...c, montantsMensuels: updateMonthInArray(c.montantsMensuels, action.mi, action.value) }
              : c
          ),
        },
      };

    case 'SALARIE_ADD':
      return { ...state, salaries: [...state.salaries, makeSalarie(state.meta.defaultTauxSalarie, totalMi)] };

    case 'SALAIRE_CATEGORIE_ADD': {
      const label = (action.label?.trim() || 'Nouvelle catégorie').trim();
      const cat: SalaireCategorie = { id: generateId(), label };
      return { ...state, salaireCategories: [...state.salaireCategories, cat] };
    }

    case 'SALAIRE_CATEGORIE_UPDATE':
      return {
        ...state,
        salaireCategories: state.salaireCategories.map((c) =>
          c.id === action.id ? { ...c, label: action.label.trim() || c.label } : c,
        ),
      };

    case 'SALAIRE_CATEGORIE_REMOVE':
      return {
        ...state,
        salaireCategories: state.salaireCategories.filter((c) => c.id !== action.id),
        salaries: state.salaries.map((s) =>
          s.categorieId === action.id ? { ...s, categorieId: null } : s,
        ),
      };

    case 'SALARIE_DUPLICATE': {
      const idx = state.salaries.findIndex((s) => s.id === action.id);
      if (idx === -1) return state;
      const copy = duplicateSalarie(state.salaries[idx], totalMi);
      const next = [...state.salaries.slice(0, idx + 1), copy, ...state.salaries.slice(idx + 1)];
      return { ...state, salaries: next };
    }

    case 'SALARIE_UPDATE':
      return {
        ...state,
        salaries: state.salaries.map((s) =>
          s.id === action.id ? { ...s, ...action.payload } : s
        ),
      };

    case 'SALARIE_REMOVE':
      return { ...state, salaries: state.salaries.filter((s) => s.id !== action.id) };

    case 'SALARIE_REMOVE_BULK':
      if (action.ids.length === 0) return state;
      return {
        ...state,
        salaries: state.salaries.filter((s) => !action.ids.includes(s.id)),
      };

    case 'SALARIE_BULK_SET_CATEGORIE': {
      if (action.ids.length === 0) return state;
      const idSet = new Set(action.ids);
      return {
        ...state,
        salaries: state.salaries.map((s) =>
          idSet.has(s.id) ? { ...s, categorieId: action.categorieId } : s,
        ),
      };
    }

    case 'SALARIE_APPLY_INFERRED_CATEGORIES': {
      const validIds = new Set(state.salaireCategories.map((c) => c.id));
      return {
        ...state,
        salaries: state.salaries.map((s) => {
          if (s.categorieId && validIds.has(s.categorieId)) return s;
          const inf = inferSalaireCategory(s, state.salaireCategories);
          if (!inf) return s;
          return { ...s, categorieId: inf.id };
        }),
      };
    }

    case 'SALARIE_BONUS_UPDATE':
      return {
        ...state,
        salaries: state.salaries.map((s) =>
          s.id === action.id
            ? { ...s, primes: updateMonthInArray(s.primes, action.mi, action.value) }
            : s
        ),
      };

    case 'DIRIGEANT_ADD':
      return { ...state, dirigeants: [...state.dirigeants, makeDirigeant(state.meta.defaultTauxDirigeant, totalMi)] };

    case 'DIRIGEANT_DUPLICATE': {
      const src = state.dirigeants.find((d) => d.id === action.id);
      if (!src) return state;
      const clone: Dirigeant = {
        ...src,
        id: generateId(),
        nom: `${src.nom} (copie)`,
        remuMensuels: [...src.remuMensuels],
        remuReels: [...src.remuReels],
        dividendes: [...src.dividendes] as [number, number, number],
      };
      return { ...state, dirigeants: [...state.dirigeants, clone] };
    }

    case 'DIRIGEANT_REMOVE':
      if (state.dirigeants.length <= 1) return state;
      return {
        ...state,
        dirigeants: state.dirigeants.filter((d) => d.id !== action.id),
      };

    case 'DIRIGEANT_UPDATE':
      return {
        ...state,
        dirigeants: state.dirigeants.map((d) =>
          d.id === action.id ? { ...d, ...action.payload } : d,
        ),
      };

    case 'DIRIGEANT_REMU_MONTH':
      return {
        ...state,
        dirigeants: state.dirigeants.map((d) =>
          d.id === action.id
            ? { ...d, remuMensuels: updateMonthInArray(d.remuMensuels, action.mi, action.value) }
            : d,
        ),
      };

    case 'DIVIDENDE_UPDATE': {
      return {
        ...state,
        dirigeants: state.dirigeants.map((d) => {
          if (d.id !== action.id) return d;
          const divs = [...d.dividendes];
          divs[action.ex] = action.value;
          return { ...d, dividendes: divs };
        }),
      };
    }

    case 'FISCALITE_UPDATE':
      return { ...state, fiscalite: { ...state.fiscalite, ...action.payload } };

    case 'CFE_UPDATE': {
      const cfe = [...state.fiscalite.cfe];
      cfe[action.ex] = action.value;
      return { ...state, fiscalite: { ...state.fiscalite, cfe } };
    }

    case 'TAXE_ADD':
      return {
        ...state,
        fiscalite: {
          ...state.fiscalite,
          autresTaxes: [
            ...state.fiscalite.autresTaxes,
            { id: generateId(), label: 'Nouvelle taxe', montant: 0, type: 'annuel' },
          ],
        },
      };

    case 'TAXE_REMOVE':
      return {
        ...state,
        fiscalite: {
          ...state.fiscalite,
          autresTaxes: state.fiscalite.autresTaxes.filter((t) => t.id !== action.id),
        },
      };

    case 'TAXE_UPDATE':
      return {
        ...state,
        fiscalite: {
          ...state.fiscalite,
          autresTaxes: state.fiscalite.autresTaxes.map((t) =>
            t.id === action.id ? { ...t, ...action.payload } : t
          ),
        },
      };

    case 'REEL_UPDATE': {
      const arr = [...(state.reel[action.field] as (number | null)[])];
      arr[action.mi] = action.value;
      return { ...state, reel: { ...state.reel, [action.field]: arr } };
    }

    case 'REVENU_REEL_UPDATE':
      return {
        ...state,
        revenus: state.revenus.map((r) =>
          r.id === action.id
            ? { ...r, montantsReels: updateNullableMonth(r.montantsReels ?? nullN(totalMi), action.mi, action.value) }
            : r
        ),
      };

    case 'CHARGE_REEL_UPDATE':
      return {
        ...state,
        charges: {
          ...state.charges,
          [action.cat]: (state.charges[action.cat] as Array<{ id: string; montantsReels: (number | null)[] }>).map((c) =>
            c.id === action.id
              ? { ...c, montantsReels: updateNullableMonth(c.montantsReels ?? nullN(totalMi), action.mi, action.value) }
              : c
          ),
        },
      };

    case 'SALARIE_REEL_UPDATE':
      return {
        ...state,
        salaries: state.salaries.map((s) =>
          s.id === action.id
            ? { ...s, brutsReels: updateNullableMonth(s.brutsReels ?? nullN(totalMi), action.mi, action.value) }
            : s
        ),
      };

    case 'DIRIGEANT_REEL_MONTH':
      return {
        ...state,
        dirigeants: state.dirigeants.map((d) =>
          d.id === action.id
            ? { ...d, remuReels: updateNullableMonth(d.remuReels ?? nullN(totalMi), action.mi, action.value) }
            : d,
        ),
      };

    case 'SALAIRES_BUDGET_MONTH_UPDATE': {
      const budget = [...(state.salairesBudget ?? Array(totalMi).fill(0))];
      while (budget.length <= action.mi) budget.push(0);
      budget[action.mi] = action.value;
      return { ...state, salairesBudget: budget };
    }

    case 'SALAIRES_BUDGET_MODE_UPDATE':
      return { ...state, salairesBudgetMode: action.mode };

    case 'SALAIRES_BUDGET_PCT_UPDATE':
      return { ...state, salairesBudgetPct: Math.max(0, Math.min(100, action.pct)) };

    case 'NOTAIRES_CONFIG_UPDATE': {
      const current = state.notairesConfig ?? createDefaultAppState().notairesConfig;
      return { ...state, notairesConfig: { ...current, ...action.payload } };
    }

    case 'NOTAIRES_SALARIES_SYNC': {
      // Remplace atomiquement toutes les lignes gérées par le module notaires,
      // MAIS préserve les `brutsReels` saisis par l'utilisateur sur un notaire auto qui
      // existe encore après resync (même `id`) — sinon l'utilisateur perdrait ses réels à
      // chaque changement de config du module.
      const manual = state.salaries.filter((s) => !s.id.startsWith('notaire-auto-'));
      const previousAutoById = new Map(
        state.salaries.filter((s) => s.id.startsWith('notaire-auto-')).map((s) => [s.id, s]),
      );
      const merged = action.salaries.map((next) => {
        const prev = previousAutoById.get(next.id);
        if (!prev) return next;
        return {
          ...next,
          brutsReels: prev.brutsReels ?? next.brutsReels,
          primes: prev.primes ?? next.primes,
        };
      });
      return { ...state, salaries: [...manual, ...merged] };
    }

    case 'SOUS_TRAITANCE_REEL_UPDATE': {
      const cfg = state.notairesConfig ?? createDefaultAppState().notairesConfig;
      const reels = Array.from(cfg.sousTraitanceReels ?? Array<number | null>(totalMi).fill(null));
      // Étend si besoin pour les mois ajoutés en cours de route.
      while (reels.length < totalMi) reels.push(null);
      if (action.mi >= 0 && action.mi < reels.length) {
        reels[action.mi] = action.value;
      }
      return { ...state, notairesConfig: { ...cfg, sousTraitanceReels: reels } };
    }

    case 'REEL_OVERRIDE_UPDATE': {
      const defaults = createDefaultAppState().reelOverrides;
      const current = state.reelOverrides ?? defaults;
      const existing = Array.from(current[action.key] ?? defaults[action.key]);
      // Étend le tableau à la taille totale courante (supporte l'ajout de mois a posteriori).
      while (existing.length < totalMi) existing.push(null);
      if (action.mi >= 0 && action.mi < existing.length) {
        existing[action.mi] = action.value;
      }
      return {
        ...state,
        reelOverrides: { ...current, [action.key]: existing },
      };
    }

    case 'UI_SET_PERIODE':
      return {
        ...state,
        ui: {
          ...state.ui,
          periode: action.periode,
          dashboardMonthFrom: null,
          dashboardMonthTo: null,
        },
      };

    case 'UI_SET_DASHBOARD_MONTH_RANGE':
      return {
        ...state,
        ui: {
          ...state.ui,
          dashboardMonthFrom: action.fromMi,
          dashboardMonthTo: action.toMi,
        },
      };

    case 'UI_SET_SYNTHESE_PERIODE':
      return { ...state, ui: { ...state.ui, synthesePeriode: action.periode } };

    case 'STATE_LOAD': {
      const salaireCategories = Array.isArray(action.state.salaireCategories)
        ? action.state.salaireCategories
        : [];
      const loadedMeta = {
        ...action.state.meta,
        formeJuridique: action.state.meta.formeJuridique ?? 'SASU',
        exercice1Duree: action.state.meta.exercice1Duree ?? 12,
        exercice2Duree: action.state.meta.exercice2Duree ?? 12,
        exercice3Duree: action.state.meta.exercice3Duree ?? 12,
        logoUrl: action.state.meta.logoUrl ?? '',
        primaryColor: action.state.meta.primaryColor ?? '#313131',
        ...(action.state.meta.logoUrl && (localStorage.setItem('app_logo_url', action.state.meta.logoUrl), {})),
        tauxIS: action.state.meta.tauxIS ?? 25,
        tauxISReduit: action.state.meta.tauxISReduit ?? 15,
        seuilISReduit: action.state.meta.seuilISReduit ?? 42500,
        tauxPFU: action.state.meta.tauxPFU ?? 30,
        defaultTauxSalarie: action.state.meta.defaultTauxSalarie ?? 42,
        defaultTauxDirigeant: action.state.meta.defaultTauxDirigeant ?? 45,
        caScenarioPct: action.state.meta.caScenarioPct ?? 100,
      };
      const loadedTotalMi = calcTotalMi(loadedMeta);
      const ch = action.state.charges;
      const noteCharge = <T extends Charge | FreelanceCharge>(c: T) => ({
        ...c,
        notes: c.notes ?? '',
        finMois: c.finMois ?? null,
        deductible: c.deductible ?? true,
        tauxTVA: c.tauxTVA ?? 20,
        variableChargeId: c.variableChargeId ?? null,
        variablePctExercices: (c as Partial<T>).variablePctExercices ?? null,
        variablePctMensuels: (c as Partial<T>).variablePctMensuels ?? null,
        progPctExercices: (c as Partial<T>).progPctExercices ?? null,
      });
      return {
        ...action.state,
        meta: loadedMeta,
        salaireCategories,
        salaries: action.state.salaries.map((s) => ({
          ...s,
          categorieId: s.categorieId ?? null,
          primes: normalizeArrayNums(s.primes, loadedTotalMi),
          notes: s.notes ?? '',
        })),
        revenus: action.state.revenus.map((r) => ({
          ...r,
          montantsMensuels: normalizeArrayNums(r.montantsMensuels, loadedTotalMi),
          montantsReels: normalizeArrayNulls(r.montantsReels, loadedTotalMi),
          progPctMensuels: normalizeArrayNums(r.progPctMensuels, loadedTotalMi),
          notes: r.notes ?? '',
        })),
        charges: {
          capex: (ch.capex ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          marketing: (ch.marketing ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          outils: (ch.outils ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          tech: (ch.tech ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          banque: (ch.banque ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          legal: (ch.legal ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          locaux: (ch.locaux ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          vehicules: (ch.vehicules ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          autres: (ch.autres ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
          freelances: (ch.freelances ?? []).map((c) => ({ ...noteCharge(c), montantsMensuels: normalizeArrayNums(c.montantsMensuels, loadedTotalMi), montantsReels: normalizeArrayNulls(c.montantsReels, loadedTotalMi) })),
        },
        investissements: Array.isArray(action.state.investissements) ? action.state.investissements : [],
        dirigeants: normalizeDirigeantsFromLoad(action.state, loadedTotalMi),
        salairesBudget: normalizeArrayNums(action.state.salairesBudget, loadedTotalMi),
        salairesBudgetMode: action.state.salairesBudgetMode ?? 'fixed',
        salairesBudgetPct: action.state.salairesBudgetPct ?? 0,
        notairesConfig: {
          ...createDefaultAppState().notairesConfig,
          ...(action.state.notairesConfig ?? {}),
        },
        reelOverrides: mergeReelOverrides(action.state.reelOverrides, loadedTotalMi),
      };
    }

    case 'STATE_RESET':
      return createDefaultAppState();

    case 'INVESTISSEMENT_ADD':
      return {
        ...state,
        investissements: [
          ...(state.investissements ?? []),
          { id: crypto.randomUUID(), ...action.payload },
        ],
      };

    case 'INVESTISSEMENT_UPDATE':
      return {
        ...state,
        investissements: (state.investissements ?? []).map((inv) =>
          inv.id === action.id ? { ...inv, ...action.payload } : inv,
        ),
      };

    case 'INVESTISSEMENT_REMOVE':
      return {
        ...state,
        investissements: (state.investissements ?? []).filter((inv) => inv.id !== action.id),
      };

    default:
      return state;
  }
}
